import React from 'react';
import type { VoiceSeedConfig } from '../types';

interface Props {
  value: VoiceSeedConfig | null;
  onChange: (next: VoiceSeedConfig | null) => void;
}

const DEFAULT_VOICE: VoiceSeedConfig = {
  type: 'voice',
  phone_number: '',
  max_duration_s: 180,
  stall_timeout_s: 8,
  stall_max_nudges: 3,
};

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, marginBottom: 4 };
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 8,
  borderRadius: 4,
  border: '1px solid #ddd',
  boxSizing: 'border-box',
};

/**
 * Voice-mode block of the seed editor. When the toggle is on the seed runs as a voice (SIP)
 * self-play call instead of the text turn loop; `onChange(null)` clears it. LiveKit creds are
 * deployment config (`SELFPLAY_VOICE_*`); only the per-call routing is configured here.
 */
const VoiceConfigForm: React.FC<Props> = ({ value, onChange }) => {
  const enabled = value !== null && value !== undefined;

  return (
    <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 12, marginTop: 16 }}>
      <legend style={{ fontSize: 13, fontWeight: 600 }}>
        <label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onChange(e.target.checked ? { ...DEFAULT_VOICE } : null)}
          />{' '}
          Voice (SIP) mode
        </label>
      </legend>

      {!enabled && (
        <p style={{ color: '#666', fontSize: 13, margin: 0 }}>
          When enabled, the batch dials the seed&apos;s phone number over SIP and runs the persona
          as a voice call instead of the text turn loop.
        </p>
      )}

      {enabled && value && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={labelStyle}>
            Phone number to dial
            <input
              type="text"
              placeholder="e.g. 0300"
              style={inputStyle}
              value={value.phone_number}
              onChange={(e) => onChange({ ...value, phone_number: e.target.value })}
            />
          </label>
          <label style={labelStyle}>
            Project (optional)
            <input
              type="text"
              placeholder="e.g. overtime"
              style={inputStyle}
              value={value.project ?? ''}
              onChange={(e) => onChange({ ...value, project: e.target.value || null })}
            />
          </label>
          <label style={labelStyle}>
            Channel (optional)
            <input
              type="text"
              placeholder="e.g. collection_en_dm10"
              style={inputStyle}
              value={value.channel ?? ''}
              onChange={(e) => onChange({ ...value, channel: e.target.value || null })}
            />
          </label>
          <label style={labelStyle}>
            Max duration (s)
            <input
              type="number"
              min={10}
              max={900}
              style={inputStyle}
              value={value.max_duration_s ?? 180}
              onChange={(e) => onChange({ ...value, max_duration_s: Number(e.target.value) })}
            />
          </label>
          <label style={labelStyle}>
            Stall timeout (s)
            <input
              type="number"
              min={2}
              max={60}
              style={inputStyle}
              value={value.stall_timeout_s ?? 8}
              onChange={(e) => onChange({ ...value, stall_timeout_s: Number(e.target.value) })}
            />
          </label>
          <label style={labelStyle}>
            Stall nudges
            <input
              type="number"
              min={0}
              max={10}
              style={inputStyle}
              value={value.stall_max_nudges ?? 3}
              onChange={(e) => onChange({ ...value, stall_max_nudges: Number(e.target.value) })}
            />
          </label>
          <label style={{ ...labelStyle, gridColumn: '1 / span 2' }}>
            End-phrase regexes (one per line)
            <textarea
              rows={3}
              style={inputStyle}
              value={(value.end_phrases ?? []).join('\n')}
              onChange={(e) => onChange({
                ...value,
                end_phrases: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
              })}
            />
          </label>
        </div>
      )}
    </fieldset>
  );
};

export default VoiceConfigForm;
